import {
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tooltip,
} from "antd";
import { debounce } from "lodash";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ReactComponent as EditIcon } from "../../assets/svg/icons/edit.svg";
import { ReactComponent as HistoryIcon } from "../../assets/svg/lead-history.svg";
import { SwapOutlined } from "@ant-design/icons";
import PlaceAutocomplete from "../../common/PlaceAutocomplete";
import { countries } from "../../constant/Countries";
import { AuthContext } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertProvider";
import { userRoles } from "../../config";
import { getLeadDetails } from "../../actionCreator/leadDetailsActionCreator";
import {
  getUnqualifiedReasons,
  searchIndustry,
  updateLeadDetails,
} from "../../actionCreator/leadListingActionCreater";
import { getUserInfo } from "../../utils/utils";
import { isValidEmail } from "../../utils";
import {
  formatAndExtractCountryCode,
  handleMobileChange,
  normalizeMobileNumber,
  prefixSelector,
  validateMobileNumber,
} from "../../utils/mobileValidation";
import {
  isLeadManagedCustomField,
  getLeadCustomFieldsState,
  getActiveLeadCustomFieldDefinitionsAPI,
  isFieldMandatory,
} from "../../utils/leadCustomFields";
import { RenderComponent } from "./CustomFields";

const { TextArea } = Input;
const whiteTooltipProps = {
  color: "#FFFFFF",
  overlayInnerStyle: {
    color: "#1F2937",
    border: "1px solid #d9dfe8",
    boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
    borderRadius: "12px",
    maxWidth: "420px",
    padding: "14px 16px",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "1.45",
  },
};

const Info = ({ onOpenHistory, onOpenTransfer }) => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const { isLeadEdit } = useContext(AuthContext);
  const [form] = Form.useForm();
  const { id: userId } = getUserInfo();
  const [editingField, setEditingField] = useState(null);
  const [hoveredField, setHoveredField] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [activeDefinitions, setActiveDefinitions] = useState([]);
  const [confirmPayload, setConfirmPayload] = useState(null);

  useEffect(() => {
    const fetchDefinitions = async () => {
      const defs = await getActiveLeadCustomFieldDefinitionsAPI();
      setActiveDefinitions(defs || []);
    };
    fetchDefinitions();
  }, []);

  const leadDetail = useSelector(
    (state) => state.LeadDetailsReducer.leadDetails,
  );
  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList,
  );
  const leadRatingListing = useSelector(
    (state) => state.leadListingReducer.leadRatingList,
  );
  const unqualifiedReasons = useSelector(
    (state) => state.leadListingReducer.unqualifiedReasons,
  );
  const usersData = useSelector((state) => state.UserManagementReducer.users);
  const users = Array.isArray(usersData) ? usersData : usersData?.content || [];

  const getLeadOwnerName = () => {
    if (!leadDetail) return "";
    if (leadDetail.ownerName) return leadDetail.ownerName;
    const empId =
      leadDetail.employeeId ||
      (typeof leadDetail.employee === "object"
        ? leadDetail.employee?.id
        : leadDetail.employee);
    if (empId) {
      const match = users.find((u) => u.id === empId);
      if (match) {
        return (
          `${match.firstName || ""} ${match.lastName || ""}`.trim() ||
          match.login
        );
      }
    }
    return "";
  };
  const leadOwnerName = getLeadOwnerName();

  const canEditLead =
    isLeadEdit && leadDetail?.allowedActions?.includes("EDIT");
  const currentUser = getUserInfo();
  const canTransferLead =
    leadDetail?.allowedActions?.includes("TRANSFER") ||
    currentUser?.role === userRoles?.admin;

  useEffect(() => {
    dispatch(getUnqualifiedReasons());
  }, [dispatch]);

  const { leadManagedCustomFields, webToLeadCustomFields } = useMemo(() => {
    const { leadCustomFields, webToLeadCustomFields: wtl } =
      getLeadCustomFieldsState(activeDefinitions, [
        ...(leadDetail?.customFields || []),
        ...(leadDetail?.wtlCustomFields || []),
      ]);
    return {
      leadManagedCustomFields: leadCustomFields,
      webToLeadCustomFields: wtl,
    };
  }, [activeDefinitions, leadDetail]);

  const fieldValues = useMemo(() => {
    const phoneNo = formatAndExtractCountryCode(leadDetail?.phoneNo);
    const mobile = formatAndExtractCountryCode(leadDetail?.mobile);

    const baseFields = {
      firstname: leadDetail?.firstname || "",
      lastname: leadDetail?.lastname || "",
      title: leadDetail?.title || "",
      phoneNo: phoneNo?.numberWithoutCountryCode || "",
      phPrefix: phoneNo?.countryLabel || "India",
      mobile: mobile?.numberWithoutCountryCode || "",
      prefix: mobile?.countryLabel || "India",
      emailId: leadDetail?.emailId || "",
      companyName: leadDetail?.companyName || "",
      website: leadDetail?.website || "",
      birthDate: leadDetail?.birthDate ? dayjs(leadDetail.birthDate) : null,
      anniversaryDate: leadDetail?.anniversaryDate
        ? dayjs(leadDetail.anniversaryDate)
        : null,
      address: leadDetail?.address || "",
      industryText: leadDetail?.industryText || "",
      rate: leadDetail?.rate || "",
      oppurtunityAmount: leadDetail?.oppurtunityAmount || "",
      status:
        leadStatusListing?.find((item) => item?.id === leadDetail?.status)
          ?.text || undefined,
      lostReason: leadDetail?.lostReason || "",
      comments: leadDetail?.comments || "",
      unqualifiedReason: leadDetail?.unqualifiedReason?.reason || "",
      unqualifiedReasonsOthers: leadDetail?.unqualifiedReasonsOthers || "",
      leadSource: leadDetail?.leadSource || "",
      description: leadDetail?.description || "",
      leadRating: leadDetail?.leadRating || undefined,
    };

    leadManagedCustomFields?.forEach((field) => {
      baseFields[`custom_${field.id}`] = field.fieldValue;
    });

    return baseFields;
  }, [leadDetail, leadStatusListing, leadManagedCustomFields]);

  useEffect(() => {
    form.setFieldsValue(fieldValues);
  }, [fieldValues, form]);

  const statusValue = Form.useWatch("status", form);
  const reasonValue = Form.useWatch("unqualifiedReason", form);

  const onSearchIndustry = useMemo(
    () =>
      debounce(async (value) => {
        if (!value) {
          setIndustryOptions([]);
          return;
        }

        const res = await dispatch(searchIndustry(value));
        if (Array.isArray(res)) {
          setIndustryOptions(res);
        }
      }, 400),
    [dispatch],
  );

  useEffect(() => {
    return () => {
      onSearchIndustry.cancel();
    };
  }, [onSearchIndustry]);

  const startEdit = (fieldName) => {
    if (fieldName === "leadOwner") {
      if (canTransferLead) {
        onOpenTransfer?.();
      } else {
        showAlert(
          "warning",
          "Permission Denied",
          "You do not have permission to transfer lead.",
        );
      }
      return;
    }
    if (!canEditLead) {
      showAlert(
        "warning",
        "Permission Denied",
        "You do not have permission to update lead.",
      );
      return;
    }

    setEditingField(fieldName);
    form.setFieldsValue(fieldValues);
  };

  const cancelEdit = () => {
    form.setFieldsValue(fieldValues);
    setEditingField(null);
  };

  const getFieldNamesForValidation = (fieldName) => {
    switch (fieldName) {
      case "phoneNo":
        return ["phoneNo", "phPrefix"];
      case "mobile":
        return ["mobile", "emailId", "prefix"];
      case "emailId":
        return ["emailId", "mobile", "prefix"];
      case "status":
        return [
          "status",
          "lostReason",
          "comments",
          "unqualifiedReason",
          "unqualifiedReasonsOthers",
        ];
      default:
        return [fieldName];
    }
  };

  const buildPayload = (fieldName, values) => {
    const payload = {
      id: leadDetail?.id,
      userId,
    };

    if (fieldName.startsWith("custom_")) {
      const fieldIdMatch = parseInt(fieldName.replace("custom_", ""), 10);
      const updatedValue = values[fieldName];

      payload.customFields = leadManagedCustomFields.map((field) => {
        if (field.id === fieldIdMatch) {
          return {
            id: field.valueId,
            fieldValue:
              typeof updatedValue === "object"
                ? dayjs(updatedValue).format("YYYY-MM-DD")
                : updatedValue,
            definition: { id: field.id },
          };
        }
        return {
          id: field.valueId,
          fieldValue: field.fieldValue,
          definition: { id: field.id },
        };
      });
      payload.wtlCustomFields = webToLeadCustomFields;
      payload.actionType = "CUSTOM_FIELD_UPDATE";
      return payload;
    }

    switch (fieldName) {
      case "phoneNo": {
        const phoneCode = countries?.find(
          (item) => item?.label === values?.phPrefix,
        )?.code;
        payload.phoneNo = values?.phoneNo
          ? phoneCode
            ? `+${phoneCode} ${values?.phoneNo}`
            : values?.phoneNo
          : null;
        break;
      }
      case "mobile": {
        const mobileCode = countries?.find(
          (item) => item?.label === values?.prefix,
        )?.code;
        payload.mobile = values?.mobile
          ? mobileCode
            ? `+${mobileCode} ${values?.mobile}`
            : values?.mobile
          : null;
        break;
      }
      case "birthDate":
      case "anniversaryDate":
        payload[fieldName] = values?.[fieldName]
          ? dayjs(values[fieldName]).format("YYYY-MM-DD")
          : null;
        break;
      case "industryText":
        payload.industryText = values?.industryText || null;
        payload.industryId =
          industryOptions?.find((item) => item?.name === values?.industryText)
            ?.id ||
          leadDetail?.industryId ||
          null;
        break;
      case "status":
        payload.status = leadStatusListing?.find(
          (item) => item?.text === values?.status,
        )?.id;
        payload.lostReason =
          values?.status === "Closed Lost" ? values?.lostReason || null : null;
        payload.comments =
          values?.status === "Closed Lost" ? values?.comments || null : null;
        payload.unqualifiedReason =
          values?.status === "Un-Qualified" && values?.unqualifiedReason
            ? {
                id: unqualifiedReasons?.find(
                  (item) => item?.reason === values?.unqualifiedReason,
                )?.id,
                reason: values?.unqualifiedReason,
              }
            : null;
        payload.unqualifiedReasonsOthers =
          values?.status === "Un-Qualified" &&
          values?.unqualifiedReason === "Others"
            ? values?.unqualifiedReasonsOthers || null
            : null;
        break;
      default:
        payload[fieldName] = values?.[fieldName] || null;
        break;
    }

    payload.actionType = "FIELD_UPDATE";
    return payload;
  };

  const CONFIRM_FIELDS = ["emailId", "mobile"];

  const saveField = async (fieldName, forceUpdate = false) => {
    try {
      const values = await form.validateFields(
        getFieldNamesForValidation(fieldName),
      );

      setIsSaving(true);
      const payload = buildPayload(fieldName, values);
      if (forceUpdate) payload.allowDuplicate = true;

      const res = await dispatch(updateLeadDetails(payload));

      if (res?.status === 200) {
        showAlert("success", "Lead updated successfully.");
        await dispatch(getLeadDetails(leadDetail?.id));
        setEditingField(null);
        setConfirmPayload(null);
      } else if (res?.response?.status === 403) {
        showAlert(
          "warning",
          "Permission Denied",
          "You do not have permission to update lead.",
        );
      } else {
        const errorMessage =
          res?.response?.data?.title || "Unable to update lead.";
        if (CONFIRM_FIELDS.includes(fieldName)) {
          setConfirmPayload({ fieldName, values, errorMessage });
        } else {
          showAlert("error", errorMessage);
        }
      }
    } catch (error) {
      return;
    } finally {
      setIsSaving(false);
    }
  };

  const forceConfirmedSave = async () => {
    if (!confirmPayload) return;
    const { fieldName, values } = confirmPayload;
    setIsSaving(true);
    try {
      const payload = buildPayload(fieldName, values);
      payload.allowDuplicate = true;
      const res = await dispatch(updateLeadDetails(payload));
      if (res?.status === 200) {
        showAlert("success", "Lead updated successfully.");
        await dispatch(getLeadDetails(leadDetail?.id));
        setEditingField(null);
      } else {
        showAlert(
          "error",
          res?.response?.data?.title || "Unable to update lead.",
        );
      }
    } finally {
      setIsSaving(false);
      setConfirmPayload(null);
    }
  };

  const renderDisplayValue = (fieldName) => {
    const value = leadDetail?.[fieldName];

    if (fieldName === "leadOwner") {
      return leadOwnerName || "-";
    }

    if (fieldName === "status") {
      return (
        leadStatusListing?.find((item) => item?.id === leadDetail?.status)
          ?.text || "-"
      );
    }

    if (fieldName === "website") {
      if (!value) return "-";

      return (
        <a
          href={value?.startsWith("http") ? value : `https://${value}`}
          target="_blank"
          rel="noreferrer"
          className="tw-text-theme hover:tw-text-theme"
        >
          {value}
        </a>
      );
    }

    if (fieldName === "birthDate" || fieldName === "anniversaryDate") {
      return value ? dayjs(value).format("DD MMM YYYY") : "-";
    }

    if (fieldName === "unqualifiedReason") {
      return leadDetail?.unqualifiedReason?.reason || "-";
    }

    return value || "-";
  };

  const getPlainDisplayValue = (fieldName) => {
    if (fieldName === "leadOwner") {
      return leadOwnerName || "-";
    }

    if (fieldName === "status") {
      return (
        leadStatusListing?.find((item) => item?.id === leadDetail?.status)
          ?.text || "-"
      );
    }

    if (fieldName === "birthDate" || fieldName === "anniversaryDate") {
      return leadDetail?.[fieldName]
        ? dayjs(leadDetail[fieldName]).format("DD MMM YYYY")
        : "-";
    }

    if (fieldName === "unqualifiedReason") {
      return leadDetail?.unqualifiedReason?.reason || "-";
    }

    return leadDetail?.[fieldName] || "-";
  };

  const renderEditor = (fieldName) => {
    switch (fieldName) {
      case "firstname":
      case "lastname":
      case "title":
      case "website":
      case "leadSource":
        return (
          <Form.Item
            name={fieldName}
            className="tw-mb-0"
            rules={
              fieldName === "firstname" || fieldName === "lastname"
                ? [{ required: true, message: "This field is required." }]
                : []
            }
          >
            <Input className="common-input" />
          </Form.Item>
        );
      case "phoneNo":
        return (
          <Form.Item
            name="phoneNo"
            className="tw-mb-0"
            normalize={normalizeMobileNumber}
            rules={[{ validator: validateMobileNumber }]}
          >
            <Input
              className="common-input"
              addonBefore={prefixSelector("phPrefix")}
              maxLength={20}
              onChange={(e) =>
                handleMobileChange(e.target.value, form, "phPrefix")
              }
            />
          </Form.Item>
        );
      case "mobile":
        return (
          <Form.Item
            name="mobile"
            className="tw-mb-0"
            normalize={normalizeMobileNumber}
            dependencies={["emailId"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const email = getFieldValue("emailId");
                  if (value || email) {
                    return Promise.resolve();
                  }
                  return Promise.reject("Please enter mobile number or email.");
                },
              }),
              { validator: validateMobileNumber },
            ]}
          >
            <Input
              className="common-input"
              addonBefore={prefixSelector()}
              maxLength={20}
              onChange={(e) => handleMobileChange(e.target.value, form)}
            />
          </Form.Item>
        );
      case "emailId":
        return (
          <Form.Item
            name="emailId"
            className="tw-mb-0"
            dependencies={["mobile"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const mobile = getFieldValue("mobile");
                  if (value || mobile) {
                    return Promise.resolve();
                  }
                  return Promise.reject("Please enter email or mobile number.");
                },
              }),
              {
                validator: (_, value) =>
                  !value || isValidEmail(value)
                    ? Promise.resolve()
                    : Promise.reject("Please enter a valid email."),
              },
            ]}
          >
            <Input className="common-input" />
          </Form.Item>
        );
      case "companyName":
        return (
          <>
            <Form.Item name="companyName" className="tw-mb-0">
              <PlaceAutocomplete
                onPlaceSelect={(place) => {
                  form.setFieldsValue({
                    companyName: place?.name,
                    website: place?.website || form.getFieldValue("website"),
                  });
                }}
              />
            </Form.Item>
            <div className="tw-mt-2 tw-text-xs tw-text-sub-text">
              Website will also update if place data provides it.
            </div>
          </>
        );
      case "birthDate":
      case "anniversaryDate":
        return (
          <Form.Item name={fieldName} className="tw-mb-0">
            <DatePicker className="tw-w-full" />
          </Form.Item>
        );
      case "address":
      case "description":
      case "comments":
      case "lostReason":
      case "unqualifiedReasonsOthers":
        return (
          <Form.Item
            name={fieldName}
            className="tw-mb-0"
            rules={
              fieldName === "lostReason" ||
              fieldName === "unqualifiedReasonsOthers"
                ? [{ required: true, message: "This field is required." }]
                : []
            }
          >
            <TextArea rows={3} className="common-input" />
          </Form.Item>
        );
      case "industryText":
        return (
          <Form.Item name="industryText" className="tw-mb-0">
            <AutoComplete
              options={industryOptions?.map((item) => ({
                label: item?.name,
                value: item?.name,
              }))}
              onSearch={onSearchIndustry}
            >
              <Input className="common-input" allowClear />
            </AutoComplete>
          </Form.Item>
        );
      case "rate":
      case "oppurtunityAmount":
        return (
          <Form.Item
            name={fieldName}
            className="tw-mb-0"
            rules={[
              {
                pattern: /^\d+(\.\d{1,2})?$/,
                message: "Please enter a valid amount.",
              },
            ]}
          >
            <Input className="common-input" />
          </Form.Item>
        );
      case "leadRating":
        return (
          <Form.Item name="leadRating" className="tw-mb-0">
            <Select
              showSearch
              options={leadRatingListing?.map((item) => ({
                label: item?.ratingText,
                value: item?.ratingText,
              }))}
            />
          </Form.Item>
        );
      case "status":
        return (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Form.Item
              name="status"
              className="tw-mb-0"
              rules={[{ required: true, message: "Please select status." }]}
            >
              <Select
                placeholder="Select status"
                options={leadStatusListing?.map((item) => ({
                  label: item?.text,
                  value: item?.text,
                }))}
                onChange={() => {
                  form.setFieldsValue({
                    lostReason: undefined,
                    comments: undefined,
                    unqualifiedReason: undefined,
                    unqualifiedReasonsOthers: undefined,
                  });
                }}
              />
            </Form.Item>

            {statusValue === "Closed Lost" && (
              <>
                <Form.Item
                  name="lostReason"
                  className="tw-mb-0"
                  rules={[{ required: true, message: "Please add a reason." }]}
                >
                  <Input className="common-input" placeholder="Lost reason" />
                </Form.Item>
                <Form.Item name="comments" className="tw-mb-0">
                  <TextArea
                    rows={3}
                    className="common-input"
                    placeholder="Comments"
                  />
                </Form.Item>
              </>
            )}

            {statusValue === "Un-Qualified" && (
              <>
                <Form.Item
                  name="unqualifiedReason"
                  className="tw-mb-0"
                  rules={[
                    { required: true, message: "Please select a reason." },
                  ]}
                >
                  <Select
                    placeholder="Reason for unqualification"
                    options={unqualifiedReasons?.map((item) => ({
                      label: item?.reason,
                      value: item?.reason,
                    }))}
                  />
                </Form.Item>
                {reasonValue === "Others" && (
                  <Form.Item
                    name="unqualifiedReasonsOthers"
                    className="tw-mb-0"
                    rules={[
                      { required: true, message: "Please add a reason." },
                    ]}
                  >
                    <TextArea rows={3} className="common-input" />
                  </Form.Item>
                )}
              </>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const renderFieldCard = (fieldName, label, { tall = false } = {}) => {
    const isEditing = editingField === fieldName;
    const plainValue = getPlainDisplayValue(fieldName);
    const isLongValue =
      typeof plainValue === "string" && plainValue.length > 60;
    const showSingleLineTooltip =
      ["description", "address", "status"].includes(fieldName) &&
      typeof plainValue === "string" &&
      plainValue !== "-" &&
      plainValue.length > 0;

    return (
      <div
        className={`group tw-transition-all tw-border-0 tw-border-b tw-border-solid tw-border-slate-100/60 hover:tw-bg-slate-50/30 tw-py-3 tw-px-2 ${
          tall ? "tw-min-h-[72px]" : ""
        }`}
        onMouseEnter={() => setHoveredField(fieldName)}
        onMouseLeave={() =>
          setHoveredField((current) => (current === fieldName ? null : current))
        }
      >
        {isEditing ? (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-text-xs tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider">
              {label}
            </div>
            {renderEditor(fieldName)}
            <div className="tw-flex tw-gap-2 tw-mt-2">
              <Button
                onClick={cancelEdit}
                className="!tw-h-8 !tw-px-3.5 !tw-rounded-lg !tw-text-xs tw-font-bold"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={isSaving}
                className="!tw-bg-theme !tw-border-theme !tw-h-8 !tw-px-3.5 !tw-rounded-lg !tw-text-xs tw-font-bold"
                onClick={() => saveField(fieldName)}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div
            className={`tw-flex tw-justify-between tw-gap-3 ${
              tall ? "tw-items-start" : "tw-items-center"
            }`}
          >
            <div className="tw-w-[140px] tw-shrink-0 tw-text-xs tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider">
              {label}
            </div>
            <div className="tw-min-w-0 tw-flex-1 tw-break-words tw-text-sm tw-font-semibold tw-text-slate-700 group-hover:tw-text-slate-900">
              {showSingleLineTooltip ? (
                <Tooltip
                  title={plainValue}
                  placement="bottomLeft"
                  {...whiteTooltipProps}
                >
                  <div className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
                    {plainValue}
                  </div>
                </Tooltip>
              ) : isLongValue ? (
                <Tooltip
                  title={plainValue}
                  placement="bottomLeft"
                  {...whiteTooltipProps}
                >
                  <div>{renderDisplayValue(fieldName)}</div>
                </Tooltip>
              ) : (
                renderDisplayValue(fieldName)
              )}
            </div>
            <div
              className={`tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-transition-opacity ${
                hoveredField === fieldName
                  ? "tw-visible tw-opacity-100"
                  : "tw-invisible tw-opacity-0"
              }`}
            >
              <Tooltip title={`View ${label} History`} {...whiteTooltipProps}>
                <button
                  type="button"
                  className="tw-flex tw-items-center tw-justify-center tw-size-7 tw-rounded-lg hover:tw-bg-slate-100 tw-text-slate-450 hover:tw-text-theme tw-cursor-pointer tw-transition-all tw-border-none tw-bg-transparent tw-outline-none"
                  onClick={() => onOpenHistory?.(fieldName)}
                >
                  <HistoryIcon className="tw-size-4" aria-hidden="true" />
                </button>
              </Tooltip>
              {fieldName === "leadOwner" ? (
                canTransferLead ? (
                  <Tooltip title="Transfer Lead Owner" {...whiteTooltipProps}>
                    <button
                      type="button"
                      className="tw-flex tw-items-center tw-justify-center tw-size-7 tw-rounded-lg hover:tw-bg-slate-100 tw-text-slate-450 hover:tw-text-theme tw-cursor-pointer tw-transition-all tw-border-none tw-bg-transparent tw-outline-none"
                      onClick={() => onOpenTransfer?.()}
                    >
                      <SwapOutlined className="tw-size-4" aria-hidden="true" />
                    </button>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title="You do not have permission to transfer lead."
                    {...whiteTooltipProps}
                  >
                    <button
                      type="button"
                      disabled
                      className="tw-flex tw-items-center tw-justify-center tw-size-7 tw-rounded-lg tw-text-slate-300 tw-cursor-not-allowed tw-border-none tw-bg-transparent tw-outline-none"
                    >
                      <SwapOutlined className="tw-size-4" aria-hidden="true" />
                    </button>
                  </Tooltip>
                )
              ) : (
                canEditLead && (
                  <Tooltip title={`Edit ${label}`} {...whiteTooltipProps}>
                    <button
                      type="button"
                      className="tw-flex tw-items-center tw-justify-center tw-size-7 tw-rounded-lg hover:tw-bg-slate-100 tw-text-slate-450 hover:tw-text-theme tw-cursor-pointer tw-transition-all tw-border-none tw-bg-transparent tw-outline-none"
                      onClick={() => startEdit(fieldName)}
                    >
                      <EditIcon className="tw-size-4" aria-hidden="true" />
                    </button>
                  </Tooltip>
                )
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const detailSections = [
    { fieldName: "firstname", label: "First Name" },
    { fieldName: "lastname", label: "Last Name" },
    { fieldName: "title", label: "Designation" },
    { fieldName: "phoneNo", label: "Phone" },
    { fieldName: "mobile", label: "Mobile" },
    { fieldName: "emailId", label: "Email" },
    { fieldName: "leadOwner", label: "Lead Owner" },
    { fieldName: "birthDate", label: "Birth Date" },
    { fieldName: "anniversaryDate", label: "Anniversary Date" },
    { fieldName: "companyName", label: "Company Name" },
    { fieldName: "website", label: "Website" },
    { fieldName: "industryText", label: "Industry" },
    { fieldName: "status", label: "Lead Status" },
    { fieldName: "rate", label: "Potential Revenue" },
    { fieldName: "oppurtunityAmount", label: "Opportunity Amount" },
    { fieldName: "leadSource", label: "Lead Source" },
    { fieldName: "leadRating", label: "Lead Rating" },
    { fieldName: "address", label: "Address" },
    { fieldName: "description", label: "Description" },
  ];

  const renderCustomFieldCard = (field, { tall = false } = {}) => {
    const fieldName = `custom_${field.id}`;
    const label = field.fieldName;
    const isEditing = editingField === fieldName;
    const plainValue = field.fieldValue || "-";
    const isLongValue =
      typeof plainValue === "string" && plainValue.length > 60;

    return (
      <div
        className={`group tw-transition-all tw-border-0 tw-border-b tw-border-solid tw-border-slate-100/60 hover:tw-bg-slate-50/30 tw-py-3 tw-px-2 ${
          tall ? "tw-min-h-[72px]" : ""
        }`}
        onMouseEnter={() => setHoveredField(fieldName)}
        onMouseLeave={() =>
          setHoveredField((current) => (current === fieldName ? null : current))
        }
      >
        {isEditing ? (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-text-xs tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider">
              {label}
            </div>
            <Form.Item
              name={fieldName}
              className="tw-mb-0"
              rules={[
                {
                  required: isFieldMandatory(fieldName, activeDefinitions),
                  message: `${label} is required`,
                },
              ]}
            >
              <RenderComponent
                fieldType={field.fieldType}
                pickValues={field.pickValues}
              />
            </Form.Item>
            <div className="tw-flex tw-gap-2 tw-mt-2">
              <Button
                onClick={cancelEdit}
                className="!tw-h-8 !tw-px-3.5 !tw-rounded-lg !tw-text-xs tw-font-bold"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={isSaving}
                className="!tw-bg-theme !tw-border-theme !tw-h-8 !tw-px-3.5 !tw-rounded-lg !tw-text-xs tw-font-bold"
                onClick={() => saveField(fieldName)}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div
            className={`tw-flex tw-justify-between tw-gap-3 ${
              tall ? "tw-items-start" : "tw-items-center"
            }`}
          >
            <div className="tw-w-[140px] tw-shrink-0 tw-text-xs tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider">
              {label}
            </div>
            <div className="tw-min-w-0 tw-flex-1 tw-break-words tw-text-sm tw-font-semibold tw-text-slate-700 group-hover:tw-text-slate-900">
              {isLongValue ? (
                <Tooltip
                  title={plainValue}
                  placement="bottomLeft"
                  {...whiteTooltipProps}
                >
                  <div>{plainValue}</div>
                </Tooltip>
              ) : (
                plainValue
              )}
            </div>
            <div
              className={`tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-transition-opacity ${
                hoveredField === fieldName
                  ? "tw-visible tw-opacity-100"
                  : "tw-invisible tw-opacity-0"
              }`}
            >
              <Tooltip title={`View ${label} History`} {...whiteTooltipProps}>
                <button
                  type="button"
                  className="tw-flex tw-items-center tw-justify-center tw-size-7 tw-rounded-lg hover:tw-bg-slate-100 tw-text-slate-450 hover:tw-text-theme tw-cursor-pointer tw-transition-all tw-border-none tw-bg-transparent tw-outline-none"
                  onClick={() => onOpenHistory?.(field.fieldName)}
                >
                  <HistoryIcon className="tw-size-4" aria-hidden="true" />
                </button>
              </Tooltip>
              {canEditLead && (
                <Tooltip title={`Edit ${label}`} {...whiteTooltipProps}>
                  <button
                    type="button"
                    className="tw-flex tw-items-center tw-justify-center tw-size-7 tw-rounded-lg hover:tw-bg-slate-100 tw-text-slate-450 hover:tw-text-theme tw-cursor-pointer tw-transition-all tw-border-none tw-bg-transparent tw-outline-none"
                    onClick={() => startEdit(fieldName)}
                  >
                    <EditIcon className="tw-size-4" aria-hidden="true" />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Form form={form} layout="vertical">
      <Modal
        open={!!confirmPayload}
        title={
          <div className="heading24px text-color-1C1C1C roboto-medium tw-pt-5 tw-px-6 tw-pb-2.5">
            Confirm Update
          </div>
        }
        className="delete-popup"
        centered
        footer={null}
        onOk={() => setConfirmPayload(null)}
        onCancel={() => setConfirmPayload(null)}
      >
        <div className="web-to-lead mt10 tw-px-6 tw-pb-5">
          <div className="text-color-434040 heading16px mt10">
            <div>{confirmPayload?.errorMessage}</div>
            <div className="tw-text-sm tw-text-sub-text tw-mt-1">
              Do you want to proceed with this update anyway?
            </div>
          </div>
          <div className="mt40 d-flex justify-content-end align-items-center tw-gap-5">
            <Button
              className="secondary-btn"
              onClick={() => setConfirmPayload(null)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="theme-btn"
              loading={isSaving}
              onClick={forceConfirmedSave}
            >
              Yes, Proceed
            </Button>
          </div>
        </div>
      </Modal>
      <div className="tw-bg-white tw-p-3 md:tw-p-4">
        <Row gutter={[12, 12]}>
          {detailSections.map((field) => (
            <Col key={field.fieldName} xs={24} md={12}>
              {renderFieldCard(field.fieldName, field.label, {
                tall: field.tall,
              })}
            </Col>
          ))}
        </Row>

        {!!leadManagedCustomFields?.length && (
          <div className="tw-mt-4">
            <div className="tw-mb-3 tw-text-sm tw-font-semibold tw-text-text-primary">
              Lead Custom Fields
            </div>
            <Row gutter={[12, 12]}>
              {leadManagedCustomFields?.map((field) => (
                <Col key={field?.fieldName} xs={24} md={12}>
                  {renderCustomFieldCard(field)}
                </Col>
              ))}
            </Row>
          </div>
        )}
      </div>
    </Form>
  );
};

export default Info;
